// db.js
// IndexedDB via Dexie — this is the app's offline source of truth.
// Pattern used throughout: read from here FIRST (instant paint),
// then refresh from the network in the background and re-write here.
//
// npm install dexie

import Dexie from 'dexie';

export const db = new Dexie('BridgenAppDB');

// ---------------------------------------------------------------------
// Schema history — the messages table's primary key changed from an
// auto-increment `id` to the message's own `_id` (see comment below).
// Dexie does NOT support changing a table's primary key within a
// single version bump — attempting that throws
// "UpgradeError: Not yet support for changing primary key" and closes
// the whole database, taking every other table down with it (which is
// why categories/contacts also broke, not just messages).
//
// The fix is a proper two-step migration: version 2 drops the old
// messages table entirely (its contents are just a disposable cache,
// safe to lose), version 3 recreates it with the new keyPath. Anyone
// whose browser is still on version 1 walks through 2 then 3
// automatically; anyone on a fresh install just gets version 3
// directly. Do not edit the version(1) block below even though its
// messages schema looks "wrong" now — it has to stay exactly as it
// was originally for the migration chain to apply correctly to
// existing users' browsers.
// ---------------------------------------------------------------------
db.version(1).stores({
  contacts: '_id',
  contactList: '_id, updatedAt, lastMessageTime',
  messages: '++id, chatId, timestamp, [chatId+timestamp]',
  categories: '_id, _type, _createdAt',
  posts: '_id, _createdAt',
  pendingActions: '++id, type, status, timestamp',
  settings: 'key',
});

db.version(2).stores({
  messages: null, // drop the old auto-increment-keyed table
});

db.version(3).stores({
  // All users the current user can message
  contacts: '_id',

  // Contacts the user has actually messaged with, sorted by recency
  contactList: '_id, updatedAt, lastMessageTime',

  // Chat messages, keyed by the message's own _id (real server id, or
  // a locally-generated "temp-..." id for an unconfirmed send — see
  // addLocalMessage/replaceLocalMessage). Using the message's own id
  // as the PRIMARY KEY means cacheMessages()'s bulkPut UPDATES an
  // existing row when the same message is re-cached (initial load,
  // background refresh, reconcile-on-reconnect all call it for
  // overlapping data) instead of inserting a new row every time.
  messages: '_id, chatId, timestamp, [chatId+timestamp]',

  // Product/category documents from Sanity, indexed by _type so a
  // single category page can be read without loading everything.
  categories: '_id, _type, _createdAt',

  // Feed posts (UploadForm)
  posts: '_id, _createdAt',

  // Outbox: anything the user did while offline that still needs to
  // reach the server. Workbox's BackgroundSyncPlugin already handles
  // retrying the raw HTTP requests — this table is for cases where
  // you want to show "sending..." / "failed, will retry" state in
  // the UI itself (e.g. a chat bubble with a clock icon).
  pendingActions: '++id, type, status, timestamp',

  // Small key/value store for misc settings (selected currency, etc.)
  // — a structured replacement for scattered localStorage.setItem calls
  settings: 'key',
});

// Adds a `currency` index to categories — additive only (the primary
// key `_id` is unchanged), so unlike the messages table's version 2/3
// migration, Dexie handles this in a single step: it just rebuilds
// the index, no drop-and-recreate needed. Needed so getCachedCategories
// can filter strictly by currency — a product's currency is fixed at
// creation (this isn't a live FX conversion, it's literally which
// currency the product was listed in), so the offline cache must
// never serve a product from a different currency than the one
// currently selected, even as a fallback when the selected currency
// has zero cached products.
db.version(4).stores({
  categories: '_id, _type, _createdAt, currency',
});

// Surface migration/open failures loudly and in one place, instead of
// every unrelated table's first read silently rejecting with an
// opaque DatabaseClosedError. This doesn't fix a bad migration, but it
// makes the failure diagnosable and gives every getCached*/setCached*
// call site a single, well-understood failure mode to guard against.
db.open().catch((err) => {
  console.error('IndexedDB failed to open (offline cache disabled this session):', err);
});

// ---------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------
export async function getCachedContacts() {
  return db.contacts.toArray();
}

export async function setCachedContacts(contacts) {
  if (!Array.isArray(contacts) || contacts.length === 0) return;
  await db.contacts.bulkPut(contacts);
}

// ---------------------------------------------------------------------
// Contact list (messaged contacts, sorted by recency)
// ---------------------------------------------------------------------
export async function getCachedContactList() {
  const all = await db.contactList.toArray();
  return all.sort(
    (a, b) =>
      new Date(b.updatedAt || b.lastMessageTime || 0) -
      new Date(a.updatedAt || a.lastMessageTime || 0)
  );
}

export async function setCachedContactList(list) {
  if (!Array.isArray(list)) return;
  await db.contactList.clear();
  if (list.length) await db.contactList.bulkPut(list);
}

export async function upsertContactListEntry(contact) {
  await db.contactList.put(contact);
}

export async function removeContactListEntry(contactId) {
  await db.contactList.delete(contactId);
}

// ---------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------
export async function getCachedMessages(chatId) {
  return db.messages.where('chatId').equals(chatId).sortBy('timestamp');
}

// Upserts by _id — since _id is now the table's primary key, calling
// this repeatedly for overlapping data (which happens constantly:
// initial load, background refresh, reconcile-on-reconnect) UPDATES
// existing rows instead of piling up duplicates.
export async function cacheMessages(chatId, messages) {
  if (!Array.isArray(messages) || messages.length === 0) return;
  const withChatId = messages.map((m) => ({ ...m, chatId }));
  await db.messages.bulkPut(withChatId);
}

// For an optimistic local send before the server has confirmed it.
// The caller MUST set message._id itself to a temporary id (e.g.
// `temp-${uuidv4()}`) before calling this — _id is the primary key
// now, so Dexie needs one supplied, it won't auto-generate it.
export async function addLocalMessage(message) {
  if (!message._id) {
    throw new Error('addLocalMessage: message._id (a temp id) is required');
  }
  await db.messages.put(message);
  return message._id;
}

// Update fields on a locally-cached message by its current _id (temp
// or real) — used to flip status pending -> sending -> sent/failed.
export async function updateLocalMessage(id, updates) {
  return db.messages.update(id, updates);
}

// Once the server confirms a send and returns the real message (with
// its real _id), swap the temp row out for the real one atomically —
// deleting the temp row and writing the real one in a single
// transaction, so there's never a moment where both exist and never
// a leftover temp-* row haunting the cache after a successful send.
export async function replaceLocalMessage(tempId, realMessage, chatId) {
  return db.transaction('rw', db.messages, async () => {
    await db.messages.delete(tempId);
    await db.messages.put({ ...realMessage, chatId, status: 'sent' });
  });
}

export async function getPendingMessages(chatId) {
  const all = await db.messages.where('chatId').equals(chatId).toArray();
  return all.filter((m) => m.status === 'pending');
}

// Clears any leftover temp-* rows for a chat — call this after a full
// reconcile-on-reconnect fetch, since that fetch's setMessages(parsed)
// is already the authoritative full history and any temp row still
// sitting in the cache at that point is stale (either its real
// counterpart already arrived in `parsed`, or it never made it to the
// server and shouldn't be shown as delivered).
export async function clearStaleLocalMessages(chatId) {
  const stale = await db.messages
    .where('chatId')
    .equals(chatId)
    .filter((m) => typeof m._id === 'string' && m._id.startsWith('temp-'))
    .toArray();
  await db.messages.bulkDelete(stale.map((m) => m._id));
}

// ---------------------------------------------------------------------
// Categories / products
// ---------------------------------------------------------------------
// currency is REQUIRED for a meaningful offline read — a product's
// listed currency is fixed data, not a live conversion, so this never
// falls back to another currency's cached products. Passing no
// currency returns everything across all currencies, which callers
// should only do deliberately (e.g. an admin/debug view), never as a
// silent fallback when the selected currency has no results.
//
// types is optional — an array of Sanity _type values to further
// narrow the result (matches the categoryName/query filtering already
// done server-side in fetchCategories). Filtered in JS rather than a
// second Dexie index, since the caller's active type list varies
// (single category page vs. the full default set) and a compound
// index for every combination isn't worth the schema complexity.
export async function getCachedCategories(currency, types) {
  const all = currency
    ? await db.categories.where('currency').equals(currency).toArray()
    : await db.categories.toArray();

  if (Array.isArray(types) && types.length) {
    return all.filter((item) => types.includes(item._type));
  }
  return all;
}

// Write-through cache — each product document already carries its own
// `currency` field from Sanity, so no extra bookkeeping is needed here
// beyond the bulkPut itself; the currency index picks it up automatically.
export async function setCachedCategories(items) {
  if (!Array.isArray(items) || items.length === 0) return;
  await db.categories.bulkPut(items);
}

// ---------------------------------------------------------------------
// Posts (feed)
// ---------------------------------------------------------------------
export async function getCachedPosts() {
  const all = await db.posts.toArray();
  return all.sort((a, b) => new Date(b._createdAt) - new Date(a._createdAt));
}

export async function setCachedPosts(posts) {
  if (!Array.isArray(posts) || posts.length === 0) return;
  await db.posts.bulkPut(posts);
}

// ---------------------------------------------------------------------
// Outbox — optimistic writes made while offline
// ---------------------------------------------------------------------
export async function queuePendingAction(type, payload) {
  return db.pendingActions.add({
    type,
    payload,
    status: 'pending',
    timestamp: Date.now(),
  });
}

export async function getPendingActions() {
  return db.pendingActions.where('status').equals('pending').toArray();
}

export async function markActionSynced(id) {
  return db.pendingActions.delete(id);
}

export async function markActionFailed(id) {
  return db.pendingActions.update(id, { status: 'failed' });
}

// ---------------------------------------------------------------------
// Settings (replaces ad-hoc localStorage.setItem calls for app state)
// ---------------------------------------------------------------------
export async function getSetting(key, fallback = null) {
  const row = await db.settings.get(key);
  return row ? row.value : fallback;
}

export async function setSetting(key, value) {
  return db.settings.put({ key, value });
}
